import cn from 'classnames';
import { useRouter } from 'next/router';
import { omit } from 'lodash';
// Constants
import { EVENT_TICKETS, STREAMS } from 'constants/routes';
// Hooks
import { useTicketingLaunched } from 'hooks/useTicketingLaunched';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Components
import FilterItem from '../FilterItem/FilterItem';
// Styles
import styles from './WatchTypePills.module.scss';

export const WATCH_TYPE_VALUES = [
  'all',
  'events',
  'ppv',
  'streams',
  'interviews',
] as const;
export type WatchTypeValue = typeof WATCH_TYPE_VALUES[number];

const WATCH_TYPE_OPTIONS: { value: WatchTypeValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'events', label: 'Tickets' },
  { value: 'ppv', label: 'PPV' },
  { value: 'streams', label: 'Streams' },
  { value: 'interviews', label: 'Interviews' },
];

/** Value used when "Interviews" pill is selected (e.g. to hide price filter). */
export const WATCH_TYPE_INTERVIEWS: WatchTypeValue = 'interviews';

type WatchTypePillsProps = {
  inModal?: boolean;
};

export const WatchTypePills = ({ inModal = false }: WatchTypePillsProps) => {
  const { filters, setFilters } = useFiltersContext();
  const { pathname, query, push } = useRouter();
  const ticketingLaunched = useTicketingLaunched();
  const options = ticketingLaunched
    ? WATCH_TYPE_OPTIONS
    : WATCH_TYPE_OPTIONS.filter(({ value }) => value !== 'events');
  // Events have their own page, so the other types are read on the Watch page.
  const isEventsPage = pathname === EVENT_TICKETS;
  const selected = isEventsPage
    ? 'events'
    : (filters?.watchType?.[0] as WatchTypeValue) || 'all';

  const onPillClick = (value: WatchTypeValue) => {
    if (value === selected) {
      return;
    }

    if (value === 'events' || isEventsPage) {
      push({
        pathname: value === 'events' ? EVENT_TICKETS : STREAMS,
        query: {
          ...omit(query, 'watchType'),
          ...(value !== 'events' && value !== 'all' && { watchType: value }),
        },
      });
      return;
    }

    setFilters({
      ...filters,
      watchType: value === 'all' ? [] : [value],
    });
  };

  return (
    <FilterItem title="Type" className={styles.root} isOpen={!inModal}>
      <div className={styles.pills} role="group" aria-label="Stream type">
        {options.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            className={cn(styles.pill, {
              [styles.pillSelected]: selected === value,
            })}
            onClick={() => onPillClick(value)}
            aria-pressed={selected === value}
          >
            {label}
          </button>
        ))}
      </div>
    </FilterItem>
  );
};

export default WatchTypePills;
